// File: client/src/pages/auth/Login.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Brain, Mail, Lock, AlertCircle, Clock } from 'lucide-react';

const formatCountdown = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;
};

const Login = () => {
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [retryAfter, setRetryAfter] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();

  const isRateLimited = retryAfter > 0;

  useEffect(() => {
    if (!isRateLimited) return;
    const timer = setInterval(() => {
      setRetryAfter(prev => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [isRateLimited]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await login(credentials);
      const encoded = btoa(encodeURIComponent(credentials.email));
      navigate(`/verify-otp?e=${encoded}`);
    } catch (err) {
      if (err.response?.status === 429) {
        const resetHeader = err.response.headers?.['ratelimit-reset'];
        const seconds = resetHeader
          ? Math.max(1, Math.ceil(parseInt(resetHeader) - Date.now() / 1000))
          : 15 * 60;
        setRetryAfter(seconds);
      } else {
        setError(err.response?.data?.message || 'Failed to login');
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-96 h-96 -top-10 -right-10 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
        <div className="absolute w-96 h-96 -bottom-10 -left-10 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
      </div>

      <div className="relative w-full max-w-md px-6 pt-10 pb-8 bg-gray-800 shadow-xl rounded-2xl space-y-8 transform hover:scale-[1.01] transition-transform duration-300">
        {/* Logo & Title */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl">
              <Brain size={40} className="text-white" />
            </div>
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 text-transparent bg-clip-text">
            Welcome Back
          </h2>
          <p className="mt-2 text-gray-400">Sign in to MLense Platform</p>
        </div>

        {/* Rate limit banner */}
        {isRateLimited && (
          <div className="p-4 bg-amber-500 bg-opacity-10 border border-amber-500 border-opacity-30 rounded-xl">
            <div className="flex items-start space-x-2 text-amber-400">
              <Clock size={20} className="mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium">Too many login attempts</p>
                <p className="text-sm mt-1">
                  You've been temporarily blocked. Try again in{' '}
                  <span className="font-bold tabular-nums">{formatCountdown(retryAfter)}</span>.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && !isRateLimited && (
          <div className="p-4 bg-red-500 bg-opacity-10 border border-red-500 border-opacity-30 rounded-xl">
            <div className="flex items-center space-x-2 text-red-500">
              <AlertCircle size={20} />
              <span className="text-sm">{error}</span>
            </div>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            {/* Email Input */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Mail size={20} className="text-gray-500" />
              </div>
              <input
                type="email"
                required
                className="w-full pl-12 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400 text-white transition-all"
                placeholder="Email address"
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
              />
            </div>

            {/* Password Input */}
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Lock size={20} className="text-gray-500" />
              </div>
              <input
                type="password"
                required
                className="w-full pl-12 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder-gray-400 text-white transition-all"
                placeholder="Password"
                value={credentials.password}
                onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
              />
            </div>
          </div>

          {/* Remember Me & Forgot Password */}
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-800"
              />
              <label className="ml-2 text-gray-400">Remember me</label>
            </div>
            <a href="/forgot-password" className="text-blue-400 hover:text-blue-300 transition-colors">
              Forgot password?
            </a>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || isRateLimited}
            className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 transform hover:-translate-y-0.5 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
          >
            {loading && (
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            {loading ? 'Sending code...' : isRateLimited ? `Try again in ${formatCountdown(retryAfter)}` : 'Sign in'}
          </button>

          {/* Sign Up Link */}
          <p className="text-center text-gray-400">
            Don't have an account?{' '}
            <a href="/register" className="text-blue-400 hover:text-blue-300 transition-colors">
              Sign up
            </a>
          </p>
        </form>
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-gray-400">
        <p className="text-sm">
          © 2026 MLense Platform. All rights reserved.
        </p>
      </div>
    </div>
  );
};



export default Login;